import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import "../../../App.css";
import Loader from "../Loader";
import Table from "../Table";
import Navbar from "../Navbar";
import { swalSuccess } from "../../../utils/swalAlert";
import { Card } from "../Card";
import demoResultData from "../GuidedTour/demoResult.json";
import Report from "../../pages/Instructor/pageUI/Results/Report";
import PerformanceReport from "../PerformanceReport";
import Modal from "../Modal";

export default function DemoResultPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const scoreData = demoResultData.score;
  const reportRef = useRef();

  // Get tour data from localStorage (user-entered values)
  const getTourData = () => {
    try {
      const stored = localStorage.getItem("guidedDemo_tourData");
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error("Error parsing tour data:", e);
    }
    return null;
  };

  const tourData = getTourData();

  // Dynamic values from tour or defaults
  const className = tourData?.className || "Demo Class";
  const studentName =
    tourData?.studentFirstName && tourData?.studentLastName
      ? `${tourData.studentFirstName} ${tourData.studentLastName}`
      : "John Doe";
  const testName = tourData?.testName || "Digital Control Systems - Mid Term";

  // Demo data for summary - using dynamic values
  const demoSummary = {
    studentName: studentName,
    testName: testName,
    classId: "demo-class-001",
    instructorName: "Demo Instructor",
    overallPerformance: `${
      studentName.split(" ")[0]
    } has demonstrated a solid understanding of the fundamental concepts. The student excels in explaining theoretical concepts and providing clear, structured answers. Performance in Part A was exceptional with 8/10 marks, showing strong command over basic definitions and theorems. In Part B, the student scored 17/20, demonstrating good analytical skills.`,
    strengths: [
      "Excellent understanding of the Final Value Theorem",
      "Clear differentiation between analog and digital control systems",
      "Comprehensive explanation of sample and hold circuit purposes",
      "Well-structured answers with proper diagrams",
      "Strong grasp of the Sampling Theorem",
    ],
    weaknesses: [
      "Confusion between Z-transform and Laplace transform",
      "Incomplete definition of Settling Time",
      "Applications could be elaborated more",
    ],
    recommendations: [
      "Review Z-transform derivations for standard functions",
      "Practice more problems on sample-and-hold devices",
      "Study time-domain and frequency-domain relationships",
      "Focus on practical applications of sampling",
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const demoInstituteInfo = {
    name: "Demo Institute",
    logoURL:
      "https://res.cloudinary.com/dfdsncmih/image/upload/v1759219779/grademe_ai_logo_sjzbso.png",
  };

  const demoClassInfo = {
    level: "Demo Level",
    className: className,
    name: className,
  };

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setLoading(false), 500);
  }, []);

  const handleCompleteDemo = () => {
    // Mark tour as completed
    localStorage.setItem("guidedDemo_completed", "true");

    swalSuccess(
      "Tour Completed! You can now explore all features on your own."
    );

    // Navigate to instructor home
    setTimeout(() => {
      navigate("/instructor");
    }, 1500);
  };

  const handleOpenAnswerPaper = () => {
    window.open("/files/Sample_Answer_Paper.pdf", "_blank");
  };

  const handleOpenTestPaper = () => {
    window.open("/files/Sample_Question_Paper.pdf", "_blank");
  };

  const handleViewSummary = () => {
    setShowSummaryModal(true);
  };

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false);
  };

  const downloadCSV = () => {
    const headers = [
      "Question Number",
      "Question Text",
      "Student Answer",
      "Obtained Score",
      "Max Score",
      "Feedback",
    ];

    const csvRows = [
      headers.join(","),
      ...scoreData.result.map((q) =>
        [
          `"${q.questionNumber}"`,
          `"${q.questionText.replace(/"/g, '""')}"`,
          `"${q.studentAnswer.replace(/"/g, '""').replace(/\n/g, " ")}"`,
          q.obtainedScore,
          q.maxScore,
          `"${q.feedback.replace(/"/g, '""')}"`,
        ].join(",")
      ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "demo_results.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintReport = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Demo_Score_Report",
  });

  const breadcrumbs = [
    { name: "Home", path: "/instructor/tour/results" },
    { name: "Tour Results", path: "/instructor/tour/results", isLast: true },
  ];

  if (loading) {
    return (
      <div className="results-page-wrapper">
        <Navbar links={[]} breadcrumbs={breadcrumbs} />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          <Loader style={{ fontSize: "50px", position: "static", left: "auto", top: "auto" }} />
        </div>
      </div>
    );
  }

  const calculateTotalScore = () => {
    const totalObtained = scoreData.result.reduce(
      (sum, q) => sum + q.obtainedScore,
      0
    );
    const totalMax = scoreData.result.reduce((sum, q) => sum + q.maxScore, 0);
    return { obtained: totalObtained, max: totalMax };
  };

  const columns = [
    { key: "questionNumber", label: "Question Number", sortable: true },
    { key: "questionText", label: "Question Text", sortable: false },
    { key: "studentAnswer", label: "Student's Answer", sortable: false },
    { key: "obtainedScore", label: "Obtained Score", sortable: true },
    { key: "maxScore", label: "Max Score", sortable: true },
    { key: "feedback", label: "Feedback", sortable: false },
  ];

  return (
    <>
      <Navbar links={[]} breadcrumbs={breadcrumbs} />
      <motion.div
        className="submitted-assessment"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
      >
        <motion.div className="upper">
          <motion.div
            className="stats-container"
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div className="stats-box">
              {[
                {
                  title: "View Answer Paper",
                  onClick: handleOpenAnswerPaper,
                  icon: "/imgs/eye_icon.svg",
                },
                {
                  title: "Total Score",
                  value: `${calculateTotalScore().obtained}/${
                    calculateTotalScore().max
                  } (${(
                    (calculateTotalScore().obtained /
                      calculateTotalScore().max) *
                    100
                  ).toFixed(1)}%)`,
                },
                {
                  title: "View Question Paper",
                  onClick: handleOpenTestPaper,
                  icon: "/imgs/eye_icon.svg",
                },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className={`stat-card`}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  onClick={stat.onClick}
                  style={{ cursor: stat.onClick ? "pointer" : "default" }}
                >
                  <h3>{stat.title}</h3>
                  <p>{stat.value}</p>
                  {stat.icon && (
                    <img
                      src={stat.icon}
                      alt="icon"
                      className="stat-img"
                      width={50}
                    />
                  )}
                  <div className="card-blur"></div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div className="stats-box">
              {[
                {
                  title: "Performance Report",
                  onClick: handleViewSummary,
                  icon: "/imgs/eye_icon.svg",
                },
                {
                  title: "Export as CSV",
                  onClick: downloadCSV,
                  icon: "/imgs/download_icon.svg",
                },
                {
                  title: "Print Scores Report",
                  onClick: handlePrintReport,
                  icon: "/imgs/print_icon.svg",
                },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className={`stat-card`}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  onClick={stat.onClick}
                  style={{ cursor: "pointer" }}
                >
                  <h3>{stat.title}</h3>
                  <p>{stat.value}</p>
                  {stat.icon && (
                    <img
                      src={stat.icon}
                      alt="icon"
                      className="stat-img"
                      width={50}
                    />
                  )}
                  <div className="card-blur"></div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="card-actions">
              <Card
                title="AI Grading Comments"
                description="This is a sample grading result. The AI has successfully graded all questions based on the sample answer paper."
              >
                <p></p>
              </Card>
            </div>
          </motion.div>
        </motion.div>
        <Table
          caption="Assessments Results"
          data={scoreData.result}
          columns={columns}
          currentPage={1}
          setCurrentPage={() => {}}
          itemsPerPage={50}
        />

        {/* Hidden Report for Printing */}
        <div style={{ display: "none" }}>
          <Report
            ref={reportRef}
            testName={testName}
            studentName={studentName}
            resultData={scoreData.result}
            instituteInfo={demoInstituteInfo}
          />
        </div>

        {/* Performance Report Modal */}
        <Modal
          isOpen={showSummaryModal}
          maxWidth="100%"
          onClose={handleCloseSummaryModal}
        >
          <PerformanceReport
            summary={demoSummary}
            isSummaryEditing={false}
            instituteInfo={demoInstituteInfo}
            classInfo={demoClassInfo}
          />
        </Modal>

        {/* Tour Complete Button */}
        <button
          onClick={handleCompleteDemo}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "rgba(255, 222, 23, 0.9)",
            padding: "0.75rem 1.25rem",
            borderRadius: "8px ",
            fontWeight: "700",
            fontSize: "0.9rem",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 6px 20px rgba(72, 187, 120, 0.5)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 14px rgba(72, 187, 120, 0.4)";
          }}
        >
          <i className="fa-solid fa-check-circle"></i>
          Complete Tour
        </button>
      </motion.div>
    </>
  );
}
